import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { IconS24Picture24 } from '@is24/cosma-ui-icons'
import { TooltipTypes } from '../../../../../@types/types'
import { MultimediaUploaderType } from '../../../../../@types/multimedia-types'
import { MultimediaPageProps } from '../../../../../@types/props-types'

import AttachmentsWrapper from './attachmentsWrapper/AttachmentsWrapper'
import { useAppSelector } from '../../../../../app/hooks'
import { InitState } from '../../../../../redux/reducers/root/initialState'
import { SCREEN_MULTIMEDIA, SCREENS } from '../../../../../services/domain/wizardScreens'
import ScreenContainer from '../../../../common/containers/screenContainer/ScreenContainer'
import { ScreenLayout } from '../../../../common/containers/screenLayout/ScreenLayout'
import MultimediaLinksManager from './links/MultimediaLinksManager'
import { ScreenTooltip } from '../../../../common/display/screenTooltip/ScreenTooltip'
import VideoHint from './videoHint/VideoHint'
import styles from './Multimedia.module.css'
import { Notification } from 'is24-corecss'

const Multimedia: React.FC<MultimediaPageProps> = ({
  allowedTypesToDisplay,
  allowMultimediaLinks = false,
  doCloseMultimediaStatusMessage,
  onNavigate,
  flowInformation,
}) => {
  const screen = SCREENS[SCREEN_MULTIMEDIA]
  const hasOnlyPictures =
    allowedTypesToDisplay.includes(MultimediaUploaderType.PICTURE) && allowedTypesToDisplay.length === 1
  const hasPicsAndVideo =
    allowedTypesToDisplay.includes(MultimediaUploaderType.PICTURE) &&
    allowedTypesToDisplay.includes(MultimediaUploaderType.VIDEO) &&
    allowedTypesToDisplay.length === 2
  const attachments = useAppSelector((state: InitState) => state.multimediaFeature.attachments)
  const statusMessage = useAppSelector((state: InitState) => state.messageFeature)
  const isWaitingListFlow = flowInformation?.options?.isWaitingListFlow
  const displayAppropriateTooltip = (tooltipType: TooltipTypes) => {
    if (isWaitingListFlow) {
      return <></>
    }
    return <ScreenTooltip tooltipType={tooltipType} tooltipKey='tooltip.screens.multimedia.common' />
  }
  const { t } = useTranslation()
  useEffect(() => {
    if (!statusMessage.multimediaMessage) return

    const timer = setTimeout(() => {
      doCloseMultimediaStatusMessage()
    }, 5000)

    return () => clearTimeout(timer)
  }, [statusMessage.multimediaMessage, doCloseMultimediaStatusMessage])

  const requiredFieldsFilledIn = true

  return (
    <ScreenContainer containerId={screen.id} className='' onNavigate={onNavigate} screen={screen}>
      <ScreenLayout
        id={`${screen.id}-desk-tooltip`}
        headline={`wizard.screen.multimedia.${hasOnlyPictures ? 'picturesHeader' : hasPicsAndVideo ? 'picsAndVideoHeader' : 'header'}`}
        subLine={t('wizard.screen.multimediaNew.subHeader')}
        screenName={screen.name}
        headerIcon={<IconS24Picture24 />}
        deskTooltip={displayAppropriateTooltip(TooltipTypes.DESKTOP)}
        mobileTooltip={displayAppropriateTooltip(TooltipTypes.MOBILE)}
      >
        {statusMessage.multimediaMessage && (
          <div className='grid-item one-whole margin-bottom-l'>
            <Notification
              title={t(`statusMessage.headlines.${statusMessage.multimediaMessage}`)}
              message={t(`statusMessage.messages.${statusMessage.multimediaMessage}`, 'Message', {
                ...statusMessage.multimediaValues,
              })}
              type={'error'}
              variant={'flat'}
              className={styles.noLeftMargin}
              onClose={doCloseMultimediaStatusMessage}
            />
          </div>
        )}

        <AttachmentsWrapper
          hasOnlyPictures={hasOnlyPictures}
          multimediaContent={attachments}
          allowedTypesToDisplay={allowedTypesToDisplay}
        />
        {!(hasOnlyPictures || isWaitingListFlow) && (
          <div className='grid-item one-whole margin-top palm-margin-top-m palm-margin-none font-body-small-regular'>
            <VideoHint requiredFieldsFilledIn={requiredFieldsFilledIn} />
          </div>
        )}
        {allowMultimediaLinks && (
          <div className='grid-item one-whole margin-top-s'>
            <MultimediaLinksManager attachments={attachments} />
          </div>
        )}
      </ScreenLayout>
    </ScreenContainer>
  )
}

export default Multimedia
